/**
 * CareStart Studio (`/dashboard/carestart-studio`) — Phase 3: variants + per-variant prompt editor.
 *
 * WHAT: Clinic Manager dashboard to create/deploy CareStart variants and customize orchestrator prompts.
 * WHY: Supervisors configure named screenings and optional AI prompt overrides without developer involvement.
 * INVARIANT: Global clinical prompts (`llmGlobalPrompts` via callLLMAPI) remain the source of truth
 *   for defaults; variant overrides live on `careStartVariants` only. (`llmQueryPrompts` is unused.)
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import HealthAndSafetyRounded from '@mui/icons-material/HealthAndSafetyRounded';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { useAuth } from '@healthdesk/shared-hooks';
import { contentCardSx } from '../../../styles/standaloneLayoutTokens';
import { fetchCareStartPrompts } from '../../../services/carestart/careStartPromptReadService';
import { getCareStartVariantsForFacility } from '../../../services/carestart/careStartVariantService';
import { CARESTART_EDITABLE_PROMPT_QUERY_IDS } from '../constants/careStartEditablePrompts';
import CareStartVariantForm from '../components/CareStartVariantForm';
import CareStartVariantList from '../components/CareStartVariantList';

export default function CareStartStudioPage() {
  const { userData } = useAuth();
  const facilityId = userData?.location || '';

  const [prompts, setPrompts] = useState([]);
  const [promptsLoading, setPromptsLoading] = useState(true);
  const [promptsError, setPromptsError] = useState(null);

  const [variants, setVariants] = useState([]);
  const [variantsLoading, setVariantsLoading] = useState(false);
  const [variantsError, setVariantsError] = useState(null);

  const editablePromptDefaults = useMemo(
    () => prompts.filter((p) => CARESTART_EDITABLE_PROMPT_QUERY_IDS.has(p.queryId)),
    [prompts]
  );

  const loadPrompts = useCallback(() => {
    setPromptsLoading(true);
    setPromptsError(null);
    fetchCareStartPrompts()
      .then((rows) => setPrompts(rows))
      .catch((err) => setPromptsError(err?.message || 'Could not load CareStart prompts.'))
      .finally(() => setPromptsLoading(false));
  }, []);

  const loadVariants = useCallback(() => {
    if (!facilityId) {
      setVariants([]);
      return;
    }
    setVariantsLoading(true);
    setVariantsError(null);
    getCareStartVariantsForFacility(facilityId)
      .then((rows) => setVariants(rows))
      .catch((err) =>
        setVariantsError(err?.message || 'Could not load your CareStart variants.')
      )
      .finally(() => setVariantsLoading(false));
  }, [facilityId]);

  useEffect(() => {
    loadPrompts();
  }, [loadPrompts]);

  useEffect(() => {
    loadVariants();
  }, [loadVariants]);

  if (!facilityId) {
    return (
      <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 640, mx: 'auto' }}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
          <HealthAndSafetyRounded color="primary" />
          <Typography variant="h5" fontWeight={700}>
            CareStart Studio
          </Typography>
        </Stack>
        <Paper elevation={0} sx={{ ...contentCardSx, p: 4, textAlign: 'center' }}>
          <LinkOffIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Link your facility first
          </Typography>
          <Typography variant="body2" color="text.secondary">
            To create a CareStart variant, your account needs to be linked to a facility. Ask your
            facility administrator to link your account, or go to Settings → Facility Info.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 960, mx: 'auto' }}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
        <HealthAndSafetyRounded color="primary" />
        <Typography variant="h5" fontWeight={700}>
          CareStart Studio
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Create shareable CareStart screenings for your facility, then review the system prompts
        that power the voice conversation.
      </Typography>

      <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
        Your screenings
      </Typography>
      {variantsLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={28} aria-label="Loading variants" />
        </Box>
      ) : variantsError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {variantsError}
        </Alert>
      ) : (
        <CareStartVariantList
          variants={variants}
          facilityId={facilityId}
          userData={userData}
          onChanged={loadVariants}
          promptDefaults={editablePromptDefaults}
        />
      )}

      <CareStartVariantForm userData={userData} onCreated={loadVariants} />

      <Divider sx={{ my: 3 }} />

      <Typography variant="h6" fontWeight={600} sx={{ mb: 0.5 }}>
        System prompts (read-only)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        These are the current system defaults for CareStart. Use &quot;Edit prompts&quot; on a variant
        above to customize AI behavior for that screening link.
      </Typography>

      {promptsLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress aria-label="Loading CareStart prompts" />
        </Box>
      ) : promptsError ? (
        <Alert severity="error">{promptsError}</Alert>
      ) : prompts.length === 0 ? (
        <Alert severity="warning">No CareStart prompts are available to display.</Alert>
      ) : (
        <Stack spacing={2}>
          {prompts.map((p) => (
            <Paper key={p.queryId} elevation={0} sx={{ ...contentCardSx, p: 2 }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                flexWrap="wrap"
                gap={1}
                sx={{ mb: 0.5 }}
              >
                <Typography variant="subtitle1" fontWeight={600}>
                  {p.label}
                </Typography>
                <Chip
                  size="small"
                  label={p.queryId}
                  variant="outlined"
                  sx={{ fontFamily: 'monospace', maxWidth: '100%' }}
                />
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                {p.description}
              </Typography>
              <Divider sx={{ mb: 1.5 }} />
              {p.exists ? (
                <TextField
                  value={p.prompt || '(This prompt has no text set.)'}
                  label="Position 1 prompt (read-only)"
                  multiline
                  fullWidth
                  minRows={4}
                  maxRows={20}
                  InputProps={{ readOnly: true }}
                  variant="outlined"
                  sx={{ '& textarea': { fontFamily: 'monospace', fontSize: 13, lineHeight: 1.5 } }}
                />
              ) : (
                <Alert severity="info" sx={{ mb: 0 }}>
                  This CareStart prompt has not been set up yet, so the conversation uses the shared
                  system default for this step.
                </Alert>
              )}
            </Paper>
          ))}
        </Stack>
      )}
    </Box>
  );
}
